import React, { useCallback, useEffect, useState } from "react";
import client, { API, formatApiError } from "@/lib/api";
import {
  Users, AlertCircle, CalendarCheck, Sparkles, Send, Download,
  ClipboardList, TrendingUp, CheckCircle2, Copy, Check, ChevronDown,
  School, BookOpen, Mic, Headphones, Brain, PlayCircle, Pause, PlayCircle as Resume,
  Search, UserPlus, Upload, X, UserX,
} from "lucide-react";
import SchoolNotices from "@/components/SchoolNotices";
import ProgressReportPanel from "@/components/ProgressReportPanel";

/* The other HikmahVerses stages. `owned` is not yet tracked per school, so the
   cross-sell shows the pathway and marks 600 Stories as the one in use. */
const PATHWAY = [
  { key: "poems", name: "Poems for Little Hearts", ages: "Ages 4–7", icon: Sparkles },
  { key: "stories", name: "600 Stories", ages: "Ages 8–13", icon: BookOpen, owned: true },
  { key: "fluency", name: "Qur'an Fluency", ages: "Reading", icon: Headphones },
  { key: "tajweed", name: "Tajweed", ages: "Recitation", icon: Mic },
  { key: "hifz", name: "Hifz with Hikmah", ages: "Memorisation", icon: Brain },
];

const Stat = ({ value, subtext, label, icon: Icon, tile = "teal", testid }) => (
  <div className={`rounded-2xl p-5 hv-tile-${tile}`} data-testid={testid}>
    {Icon && (
      <div className="hv-tile-icon text-hv-navy">
        <Icon size={18} strokeWidth={2.5} />
      </div>
    )}
    <div className={`hv-heading text-hv-navy mt-2 ${Icon ? "" : "mt-0"} text-3xl sm:text-4xl`}>{value}</div>
    {subtext && <div className="text-[11px] text-hv-slate mt-0.5">{subtext}</div>}
    <div className="text-[11px] uppercase tracking-wider text-hv-slate mt-1 font-bold">{label}</div>
  </div>
);

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

export function AttendanceModal({ scope, onClose, onSaved }) {
  const [date, setDate] = useState(todayIso());
  const [roster, setRoster] = useState(null);
  const [msg, setMsg] = useState("");
  const [loadErr, setLoadErr] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoadErr("");
    try {
      const { data } = await client.get("/teacher/attendance", {
        params: { scope_type: scope.scope_type, scope_id: scope.scope_id, date },
      });
      setRoster(data.roster);
    } catch (ex) {
      setLoadErr(formatApiError(ex.response?.data?.detail) || ex.message);
    }
  }, [scope, date]);

  useEffect(() => { load(); }, [load]);

  const toggle = (id) => {
    setRoster((prev) => prev.map((r) => (r.id === id ? { ...r, present: !r.present } : r)));
  };
  const markAll = (present) => setRoster((prev) => prev.map((r) => ({ ...r, present })));

  const presentCount = (roster || []).filter((r) => r.present === true).length;

  const save = async () => {
    setBusy(true);
    const records = roster.filter((r) => r.present !== null).map((r) => ({ student_id: r.id, present: !!r.present }));
    try {
      const { data } = await client.post("/teacher/attendance", {
        scope_type: scope.scope_type, scope_id: scope.scope_id, date, records,
      });
      setMsg(`Saved attendance for ${data.saved} student(s).`);
      onSaved?.();
      setTimeout(() => onClose(), 700);
    } catch (ex) {
      setMsg(formatApiError(ex.response?.data?.detail) || ex.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-hv-navy/50" data-testid="attendance-modal">
      <div className="hv-card w-full max-w-lg max-h-[90vh] overflow-y-auto p-6 sm:p-7">
        <div className="flex items-start justify-between gap-3">
          <h3 className="hv-heading text-lg text-hv-navy inline-flex items-center gap-2">
            <CalendarCheck size={20} strokeWidth={2.5} className="text-hv-teal" />
            Take attendance — {scope.class_name ? `${scope.class_name} / ` : ""}{scope.name}
          </h3>
          <button onClick={onClose} data-testid="attendance-modal-close" className="text-hv-slate hover:text-hv-coral shrink-0">
            <X size={20} strokeWidth={2.5} />
          </button>
        </div>
        <p className="text-hv-slate text-sm mt-3">
          This is a record only — it doesn&apos;t unlock anything. Absent students&apos; own stories stay on schedule
          too; they just fall behind until home catches them up.
        </p>

        <label className="block mt-5 text-xs font-bold uppercase tracking-wider text-hv-slate">Date</label>
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="attendance-date"
               className="mt-1.5 w-full rounded-xl border-2 border-hv-teal px-3 py-2.5 bg-hv-teal-tint text-sm font-bold text-hv-navy" />

        {loadErr && <div className="mt-4 text-hv-coral text-sm font-bold">{loadErr}</div>}
        {!roster && !loadErr && <div className="mt-4 text-hv-slate text-sm">Loading…</div>}
        {roster && (
          <>
            <div className="mt-5 flex items-center justify-between text-sm">
              <span className="font-bold text-hv-navy">{presentCount} / {roster.length} present</span>
              <span className="flex gap-3">
                <button onClick={() => markAll(true)} data-testid="attendance-mark-all-present" className="text-hv-teal font-bold hover:underline">Mark all present</button>
                <button onClick={() => markAll(false)} data-testid="attendance-mark-all-absent" className="text-hv-coral font-bold hover:underline">Mark all absent</button>
              </span>
            </div>

            <div className="mt-3 space-y-2">
              {roster.map((r) => (
                <div key={r.id} data-testid={`attendance-row-${r.id}`}
                     className={`rounded-2xl border-2 px-3.5 py-2.5 flex items-center justify-between gap-3 ${
                       r.present === true ? "border-hv-teal/50 bg-hv-teal-tint"
                       : r.present === false ? "border-hv-coral/40 bg-[#FFF3F0]" : "border-[#E5DFC8] bg-white"}`}>
                  <span className="inline-flex items-center gap-2.5 min-w-0">
                    <span className="w-8 h-8 rounded-full bg-white flex items-center justify-center font-bold text-hv-navy text-xs shrink-0 shadow-sm">
                      {r.name.slice(0, 1).toUpperCase()}
                    </span>
                    <span className="font-bold text-hv-navy truncate">{r.name}</span>
                  </span>
                  <button onClick={() => toggle(r.id)} data-testid={`attendance-${r.id}`}
                          className={`hv-pill px-3 py-1.5 text-xs font-bold inline-flex items-center gap-1.5 shrink-0 ${
                            r.present === true ? "bg-hv-teal text-white"
                            : r.present === false ? "bg-[#FDE4DE] text-hv-coral" : "bg-white border-2 border-[#E5DFC8] text-hv-slate"}`}>
                    {r.present === true ? <><Check size={13} strokeWidth={3} /> Present</>
                      : r.present === false ? <><UserX size={13} strokeWidth={3} /> Absent</>
                      : "Tap to mark"}
                  </button>
                </div>
              ))}
            </div>

            <div className="mt-6 flex items-center justify-end gap-4">
              {msg && <span className="text-sm font-bold text-hv-teal me-auto">{msg}</span>}
              <button onClick={onClose} className="text-hv-slate font-bold text-sm">Cancel</button>
              <button onClick={save} disabled={busy} data-testid="attendance-save" className="hv-btn-primary">
                {busy ? "Recording…" : `Record attendance for ${date}`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function ClassDetail({ scope, onBack, onChanged }) {
  const [data, setData] = useState(null);
  const [pulse, setPulse] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const [guide, setGuide] = useState(null);
  const [broadcast, setBroadcast] = useState(null);
  const [copied, setCopied] = useState(false);
  const [roster, setRoster] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [loadErr, setLoadErr] = useState("");
  const [showAttendance, setShowAttendance] = useState(false);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [showAddStudent, setShowAddStudent] = useState(false);
  const [addName, setAddName] = useState("");
  const [addEmail, setAddEmail] = useState("");
  const [addParent, setAddParent] = useState("");
  const [rosterQ, setRosterQ] = useState("");

  const load = useCallback(async () => {
    setLoadErr("");
    try {
      const [d, p, r] = await Promise.all([
        client.get("/teacher/section-summary", { params: { scope_type: scope.scope_type, scope_id: scope.scope_id } }),
        client.get("/teacher/pulse", { params: { scope_type: scope.scope_type, scope_id: scope.scope_id } }),
        client.get("/teacher/readiness", { params: { scope_type: scope.scope_type, scope_id: scope.scope_id } }),
      ]);
      setData(d.data); setPulse(p.data); setReadiness(r.data);
    } catch (ex) {
      setLoadErr(formatApiError(ex.response?.data?.detail) || ex.message);
    }
  }, [scope]);

  useEffect(() => { load(); }, [load]);

  const started = data?.scope?.doc?.paced_started_at;
  const paused = data?.scope?.doc?.paced_paused;

  const startRelease = async () => {
    setBusy(true);
    try {
      await client.post("/teacher/start-release", { scope_type: scope.scope_type, scope_id: scope.scope_id });
      await load(); onChanged();
    } finally { setBusy(false); }
  };
  const togglePause = async () => {
    setBusy(true);
    try {
      await client.post(paused ? "/teacher/release-resume" : "/teacher/release-pause",
        { scope_type: scope.scope_type, scope_id: scope.scope_id });
      await load(); onChanged();
    } finally { setBusy(false); }
  };
  const markComplete = async () => {
    setBusy(true);
    try {
      const { data: res } = await client.post("/teacher/mark-class-progress",
        { scope_type: scope.scope_type, scope_id: scope.scope_id });
      setMsg(`Marked story #${res.global_sequence} complete for ${res.marked} student(s).`);
      load();
    } catch (ex) {
      setMsg(formatApiError(ex.response?.data?.detail) || ex.message);
    } finally { setBusy(false); }
  };

  // The lighter one-straggler tool next to the batch "Mark today's story
  // complete" button above — a parent who genuinely can't do today's story
  // at home with her child shouldn't hold the rest of the class back.
  const [markingStudent, setMarkingStudent] = useState(null);
  const markStudentDone = async (studentId, studentName) => {
    setMarkingStudent(studentId);
    try {
      const { data: res } = await client.post("/teacher/mark-student-complete",
        { scope_type: scope.scope_type, scope_id: scope.scope_id, student_id: studentId });
      setMsg(res.already_done ? `${studentName} was already done with story #${res.global_sequence}.`
                               : `Marked story #${res.global_sequence} complete for ${studentName}.`);
      load();
    } catch (ex) {
      setMsg(formatApiError(ex.response?.data?.detail) || ex.message);
    } finally { setMarkingStudent(null); }
  };

  const importRoster = async () => {
    const students = roster.split("\n").map((line) => {
      const [email, name, parent_contact] = line.split(",").map((x) => (x || "").trim());
      return email ? { email, name: name || undefined, parent_contact: parent_contact || undefined } : null;
    }).filter(Boolean);
    if (!students.length) return;
    try {
      const { data: res } = await client.post("/teacher/section-students",
        { scope_type: scope.scope_type, scope_id: scope.scope_id, students });
      setMsg(`Added ${res.created} new, updated ${res.updated}.`);
      setRoster("");
      load();
    } catch (ex) {
      setMsg(formatApiError(ex.response?.data?.detail) || ex.message);
    }
  };

  const addStudent = async () => {
    if (!addEmail.trim()) return;
    try {
      const { data: res } = await client.post("/teacher/section-students", {
        scope_type: scope.scope_type, scope_id: scope.scope_id,
        students: [{ email: addEmail.trim(), name: addName.trim() || undefined, parent_contact: addParent.trim() || undefined }],
      });
      setMsg(`Added ${res.created} new, updated ${res.updated}.`);
      setAddName(""); setAddEmail(""); setAddParent(""); setShowAddStudent(false);
      load();
    } catch (ex) {
      setMsg(formatApiError(ex.response?.data?.detail) || ex.message);
    }
  };

  const copyBroadcast = async () => {
    try { await navigator.clipboard.writeText(broadcast.message); setCopied(true); setTimeout(() => setCopied(false), 2500); } catch { /* ignore */ }
  };

  if (loadErr && !data) {
    return (
      <div className="p-12" data-testid="class-detail-error">
        <button onClick={onBack} className="text-hv-teal font-bold text-sm inline-flex items-center gap-1">
          <ChevronDown size={16} className="rotate-90" /> All classes
        </button>
        <p className="mt-4 text-hv-coral font-bold">{loadErr}</p>
        <button onClick={load} className="hv-btn-secondary mt-3">Try again</button>
      </div>
    );
  }
  if (!data) return <div className="p-12 text-hv-slate">Loading…</div>;

  return (
    <div data-testid="class-detail">
      <button onClick={onBack} data-testid="class-back" className="text-hv-teal font-bold text-sm inline-flex items-center gap-1">
        <ChevronDown size={16} className="rotate-90" /> All classes
      </button>

      <div className="mt-4 flex flex-wrap items-end justify-between gap-4">
        <div>
          <span className="hv-badge">{scope.class_name || "Your class"}</span>
          <h1 className="hv-heading mt-3 text-3xl sm:text-4xl text-hv-navy">{scope.name}</h1>
        </div>
        <a href={`${API}/teacher/report.csv?scope_type=${scope.scope_type}&scope_id=${scope.scope_id}`}
           data-testid="export-csv" className="hv-btn-secondary inline-flex items-center gap-2">
          <Download size={18} strokeWidth={2.5} /> Export completion report
        </a>
      </div>

      <ProgressReportPanel path="/teacher/report" params={{ scope_type: scope.scope_type, scope_id: scope.scope_id }} fileBase={scope.name} subtitle="every student in this class" />

      {/* Pacing controls — the school-paced clock for this one class */}
      <div className="mt-6 hv-card p-6 flex flex-wrap items-center gap-4" data-testid="pacing-controls">
        {!started ? (
          <button onClick={startRelease} disabled={busy} data-testid="start-release" className="hv-btn-primary inline-flex items-center gap-2">
            <PlayCircle size={18} strokeWidth={2.5} /> Start from today
          </button>
        ) : (
          <>
            <span className="text-sm text-hv-slate">
              Started <b className="text-hv-navy">{started}</b> · Day {pulse?.day_in_cycle ?? "—"} of the cycle
              {paused && <span className="text-hv-coral font-bold"> · paused</span>}
            </span>
            <button onClick={togglePause} disabled={busy} data-testid="toggle-pause" className="hv-btn-secondary inline-flex items-center gap-2">
              {paused ? <><Resume size={16} strokeWidth={2.5} /> Resume</> : <><Pause size={16} strokeWidth={2.5} /> Pause (exam break)</>}
            </button>
            <button onClick={markComplete} disabled={busy} data-testid="mark-class-progress" className="hv-btn-secondary inline-flex items-center gap-2">
              <CheckCircle2 size={16} strokeWidth={2.5} /> Mark today complete for class
            </button>
          </>
        )}
        {msg && <span className="text-sm font-bold text-hv-teal">{msg}</span>}
      </div>

      {/* The two numbers that matter, first */}
      <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat testid="stat-completion" value={`${data.stats.completion_rate}%`} label="Completion" icon={TrendingUp} tile="teal" />
        <Stat testid="stat-attention" value={data.stats.needs_attention} label="Need attention" icon={AlertCircle}
              tile={data.stats.needs_attention ? "coral" : "teal"} />
        <Stat testid="stat-students" value={data.stats.student_count} label="Students" icon={Users} tile="gold" />
        <Stat testid="stat-watched" value={`${pulse?.watched_pct ?? 0}%`} label="Watched this week" icon={PlayCircle} tile="lavender" />
      </div>

      {/* Class pulse + readiness */}
      <div className="mt-6 grid lg:grid-cols-2 gap-5">
        <div className="hv-card p-7" data-testid="class-pulse">
          <div className="flex items-center gap-3">
            <div className="hv-icon-circle w-11 h-11 rounded-full flex items-center justify-center">
              <TrendingUp size={20} strokeWidth={2.5} />
            </div>
            <span className="hv-badge">Class pulse</span>
          </div>
          <h2 className="hv-heading mt-4 text-xl text-hv-navy">
            {pulse?.watched_this_week ?? 0} of {pulse?.student_count ?? 0} finished this week&apos;s stories
          </h2>
          {pulse?.hardest_question ? (
            <div className="mt-4 rounded-2xl bg-[#FDF4DC] border border-[#E5DFC8] p-4" data-testid="hardest-question">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#B27E0A] font-bold">Most-missed question</div>
              <div className="hv-heading text-hv-navy text-sm mt-1.5">{pulse.hardest_question.question}</div>
              <div className="text-hv-slate text-xs mt-1">
                Missed by {pulse.hardest_question.missed_by} · {pulse.hardest_question.story_title}
              </div>
            </div>
          ) : (
            <p className="mt-4 text-hv-slate text-sm">No quiz misses yet this week — nothing to reteach.</p>
          )}
        </div>

        <div className="hv-card p-7" data-testid="readiness">
          <div className="flex items-center gap-3">
            <div className="hv-icon-circle-coral w-11 h-11 rounded-full flex items-center justify-center">
              <CalendarCheck size={20} strokeWidth={2.5} />
            </div>
            <span className="hv-badge hv-badge-coral">Before the live day</span>
          </div>
          <h2 className="hv-heading mt-4 text-xl text-hv-navy">
            {readiness?.ready_count ?? 0} of {readiness?.student_count ?? 0} ready · {readiness?.days_to_live ?? 0} days to go
          </h2>
          {readiness?.needs_attention?.length ? (
            <ul className="mt-4 space-y-2">
              {readiness.needs_attention.slice(0, 5).map((s) => (
                <li key={s.id} className="flex items-center gap-2 text-sm text-hv-navy" data-testid={`attention-${s.id}`}>
                  <AlertCircle size={16} className="text-hv-coral shrink-0" strokeWidth={2.5} />
                  <span className="font-bold">{s.name}</span>
                  <span className="text-hv-slate">— {s.behind} behind</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-4 text-hv-slate text-sm">Everyone is on track. Walk in relaxed.</p>
          )}
        </div>
      </div>

      {/* Roster — the screen a teacher opens most */}
      <div className="mt-6 hv-card p-7" data-testid="roster">
        <div className="flex items-center gap-3">
          <div className="hv-icon-circle w-11 h-11 rounded-full flex items-center justify-center">
            <Users size={20} strokeWidth={2.5} />
          </div>
          <span className="hv-badge">Roster · sorted by who needs you</span>
        </div>

        {/* Action bar */}
        <div className="mt-5 flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[180px]">
            <Search size={15} className="absolute start-3 top-1/2 -translate-y-1/2 text-hv-slate" />
            <input value={rosterQ} onChange={(e) => setRosterQ(e.target.value)} placeholder="Search students by name"
                   data-testid="roster-search" className="w-full ps-9 pe-3 py-2.5 rounded-full border-2 border-[#E5DFC8] focus:outline-none focus:border-hv-teal bg-white text-sm" />
          </div>
          <button onClick={() => setShowAttendance(true)} data-testid="open-attendance"
                  className="rounded-full bg-hv-navy text-white min-h-[44px] px-4 text-sm font-bold inline-flex items-center gap-2">
            <ClipboardList size={16} strokeWidth={2.5} /> Take attendance
          </button>
          <button onClick={() => setShowAddStudent((v) => !v)} data-testid="open-add-student" className="hv-btn-primary inline-flex items-center gap-2 py-2.5">
            <UserPlus size={16} strokeWidth={2.5} /> Add a student
          </button>
          <button onClick={() => setShowBulkImport((v) => !v)} data-testid="open-bulk-import" className="hv-btn-secondary inline-flex items-center gap-2 py-2.5">
            <Upload size={16} strokeWidth={2.5} /> Bulk import (CSV)
          </button>
        </div>

        {showAddStudent && (
          <div className="mt-4 rounded-2xl border-2 border-[#E5DFC8] p-4" data-testid="add-student-form">
            <div className="grid sm:grid-cols-3 gap-3">
              <input value={addName} onChange={(e) => setAddName(e.target.value)} placeholder="Name"
                     data-testid="add-student-name" className="px-3 py-2.5 rounded-xl border-2 border-[#E5DFC8] focus:outline-none focus:border-hv-teal bg-white text-sm" />
              <input value={addEmail} onChange={(e) => setAddEmail(e.target.value)} placeholder="Email"
                     data-testid="add-student-email" className="px-3 py-2.5 rounded-xl border-2 border-[#E5DFC8] focus:outline-none focus:border-hv-teal bg-white text-sm" />
              <input value={addParent} onChange={(e) => setAddParent(e.target.value)} placeholder="Parent contact (optional)"
                     data-testid="add-student-parent" className="px-3 py-2.5 rounded-xl border-2 border-[#E5DFC8] focus:outline-none focus:border-hv-teal bg-white text-sm" />
            </div>
            <button onClick={addStudent} data-testid="add-student-submit" className="hv-btn-primary mt-3">Add student</button>
          </div>
        )}

        {showBulkImport && (
          <div className="mt-4 rounded-2xl border-2 border-[#E5DFC8] p-4">
            <div className="hv-heading text-hv-navy text-sm">Bulk roster import</div>
            <p className="text-hv-slate text-xs mt-1">One per line: <code>email, name, parent contact</code></p>
            <textarea value={roster} onChange={(e) => setRoster(e.target.value)} rows={4}
                      data-testid="roster-input"
                      placeholder={"aisha@example.com, Aisha, +44...\nyusuf@example.com, Yusuf"}
                      className="mt-2 w-full rounded-2xl border-2 border-[#E5DFC8] px-4 py-3 bg-white text-sm" />
            <button onClick={importRoster} data-testid="roster-import" className="hv-btn-secondary mt-3 inline-flex items-center gap-2">
              <ClipboardList size={18} strokeWidth={2.5} /> Import students
            </button>
          </div>
        )}

        {msg && <p className="mt-4 text-sm font-bold text-hv-teal">{msg}</p>}

        <div className="mt-5 space-y-2">
          {data.roster.filter((s) => !rosterQ.trim() || s.name.toLowerCase().includes(rosterQ.trim().toLowerCase())).map((s) => {
            const pct = s.unlocked ? Math.round((100 * s.completed) / s.unlocked) : 0;
            return (
              <div key={s.id} data-testid={`roster-row-${s.id}`}
                   className={`rounded-2xl border-2 p-4 flex items-center gap-4 ${
                     s.needs_attention ? "border-hv-coral/40 bg-[#FFF3F0]" : "border-[#E5DFC8] bg-white"}`}>
                <div className={`w-11 h-11 rounded-full flex items-center justify-center shrink-0 font-bold text-white ${
                  s.needs_attention ? "bg-hv-coral" : "bg-hv-teal"}`}>
                  {s.name.slice(0, 1).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="hv-heading text-hv-navy">{s.name}</div>
                  <div className="mt-1.5 h-2 rounded-full bg-[#E5DFC8] overflow-hidden">
                    <div className={`h-full rounded-full ${s.needs_attention ? "bg-hv-coral" : "bg-hv-teal"}`}
                         style={{ width: `${pct}%` }} />
                  </div>
                </div>
                <div className="text-end shrink-0">
                  <div className="hv-heading text-hv-navy">{s.completed}/{s.unlocked}</div>
                  {s.needs_attention
                    ? <div className="text-xs font-bold text-hv-coral">needs attention</div>
                    : <div className="text-xs text-hv-slate">on track</div>}
                </div>
                {/* One-straggler override — lighter than the batch "Mark
                    today's story complete" button above: for the one child
                    whose parent genuinely can't do a story with her at
                    home, without inventing a score or holding the rest of
                    the class's pacing hostage. */}
                {s.behind > 0 && (
                  <button
                    onClick={() => markStudentDone(s.id, s.name)}
                    disabled={markingStudent === s.id}
                    data-testid={`mark-student-done-${s.id}`}
                    title="Mark today's story complete for just this student"
                    className="hv-btn-secondary shrink-0 py-2 px-3 text-xs inline-flex items-center gap-1.5 disabled:opacity-50"
                  >
                    <CheckCircle2 size={14} strokeWidth={2.5} />
                    {markingStudent === s.id ? "Marking…" : "Mark done"}
                  </button>
                )}
              </div>
            );
          })}
          {!data.roster.length && <p className="text-hv-slate text-sm">No students yet — add one above.</p>}
        </div>
      </div>

      {showAttendance && (
        <AttendanceModal scope={scope} onClose={() => setShowAttendance(false)} onSaved={load} />
      )}

      {/* Discussion guide + parent broadcast */}
      <div className="mt-6 grid lg:grid-cols-2 gap-5">
        <div className="hv-card p-7" data-testid="discussion-guide">
          <span className="hv-badge">Discussion guide</span>
          <button onClick={async () => setGuide((await client.get("/teacher/discussion-guide",
                    { params: { scope_type: scope.scope_type, scope_id: scope.scope_id } })).data)}
                  data-testid="load-guide" className="hv-btn-secondary mt-4 inline-flex items-center gap-2">
            <Sparkles size={18} strokeWidth={2.5} /> {guide ? "Refresh guide" : "Generate this week's guide"}
          </button>
          {guide?.guide?.map((g) => (
            <div key={g.story_seq} className="mt-5 border-t border-[#E5DFC8] pt-4" data-testid={`guide-${g.story_seq}`}>
              <div className="hv-heading text-hv-navy">{g.title}</div>
              <div className="text-hv-slate text-xs mt-1">{g.ayah.reference} — {g.ayah.translation}</div>
              <ul className="mt-3 space-y-1.5">
                {g.discussion_questions.map((q, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-hv-navy">
                    <CheckCircle2 size={15} className="text-hv-teal mt-0.5 shrink-0" strokeWidth={2.5} /> {q}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="hv-card p-7" data-testid="broadcast">
          <span className="hv-badge">Parent broadcast</span>
          <button onClick={async () => setBroadcast((await client.post("/teacher/broadcast",
                    { scope_type: scope.scope_type, scope_id: scope.scope_id })).data)}
                  data-testid="load-broadcast" className="hv-btn-secondary mt-4 inline-flex items-center gap-2">
            <Send size={18} strokeWidth={2.5} /> Compose this week&apos;s message
          </button>
          {broadcast && (
            <div className="mt-5">
              <div className="rounded-2xl bg-[#FBF6E4] border border-[#E5DFC8] p-4 text-sm text-hv-navy leading-relaxed"
                   data-testid="broadcast-message">
                {broadcast.message}
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-3">
                <button onClick={copyBroadcast} data-testid="copy-broadcast" className="hv-btn-secondary inline-flex items-center gap-2">
                  {copied ? <Check size={18} strokeWidth={2.5} /> : <Copy size={18} strokeWidth={2.5} />}
                  {copied ? "Copied" : "Copy message"}
                </button>
                <span className="text-hv-slate text-xs">
                  {broadcast.recipient_count} parents on file
                  {broadcast.recipients_without_contact > 0 && ` · ${broadcast.recipients_without_contact} missing a contact`}
                </span>
              </div>
              <p className="mt-3 text-hv-slate text-xs">{broadcast.note}</p>
            </div>
          )}
        </div>
      </div>

      {/* School notices */}
      <div className="mt-6 hv-card p-7">
        <SchoolNotices schoolId={scope.school_id} />
      </div>
    </div>
  );
}

export default function TeacherDashboard() {
  const [scopes, setScopes] = useState(null);
  const [active, setActive] = useState(null);
  const [loadErr, setLoadErr] = useState("");

  const load = useCallback(async () => {
    setLoadErr("");
    try {
      const { data } = await client.get("/teacher/scopes");
      setScopes(data.scopes);
    } catch (ex) {
      setLoadErr(formatApiError(ex.response?.data?.detail) || ex.message);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (active) {
    return (
      <main className="max-w-6xl mx-auto px-5 sm:px-8 py-10" data-testid="teacher-dashboard">
        <ClassDetail scope={active} onBack={() => { setActive(null); load(); }} onChanged={load} />
      </main>
    );
  }

  return (
    <main className="max-w-6xl mx-auto px-5 sm:px-8 py-10" data-testid="teacher-dashboard">
      <span className="hv-badge">Your classes</span>
      <h1 className="hv-heading mt-4 text-3xl sm:text-4xl">
        <span className="text-hv-navy">Classes</span> <span className="text-hv-coral">at a glance.</span>
      </h1>
      <p className="mt-3 text-hv-slate text-sm">
        Classes are created for you by a School Admin or your Principal — pick one below to take attendance,
        start pacing, and manage your roster.
      </p>

      <div className="mt-6 grid md:grid-cols-2 gap-5">
        {(scopes || []).map((c) => (
          <button key={c.scope_id} onClick={() => setActive(c)} data-testid={`class-card-${c.scope_id}`}
                  className="hv-card p-7 text-start transition hover:-translate-y-1 hover:shadow-lg">
            <div className="flex items-start justify-between gap-3">
              <div>
                <span className="hv-badge">{c.class_name || "Class"}</span>
                <h2 className="hv-heading mt-3 text-xl text-hv-navy">{c.name}</h2>
              </div>
              <div className="text-end shrink-0">
                {c.started ? (
                  <>
                    <div className="hv-heading text-xl text-hv-teal">Day {c.day_in_cycle}</div>
                    <div className="text-[10px] uppercase tracking-wider text-hv-slate font-bold">
                      {c.paused ? "paused" : "running"}
                    </div>
                  </>
                ) : (
                  <div className="text-[10px] uppercase tracking-wider text-hv-coral font-bold">not started</div>
                )}
              </div>
            </div>
            <div className="mt-5 flex flex-wrap gap-4 text-sm">
              <span className="inline-flex items-center gap-1.5 text-hv-slate">
                <Users size={15} strokeWidth={2.5} className="text-hv-teal" /> {c.student_count} students
              </span>
            </div>
          </button>
        ))}
        {scopes && !scopes.length && (
          <div className="hv-card p-7 text-hv-slate">
            No classes yet. Ask your School Admin or Principal to assign you to a section.
          </div>
        )}
        {loadErr && (
          <div className="hv-card p-7 text-hv-coral col-span-2" data-testid="scopes-error">
            {loadErr} <button onClick={load} className="hv-btn-secondary ms-3 py-1 px-3 text-xs">Try again</button>
          </div>
        )}
        {!scopes && !loadErr && <div className="p-6 text-hv-slate">Loading…</div>}
      </div>

      {/* Ecosystem adoption — which stages this school already has */}
      <div className="mt-12 hv-card p-7" data-testid="ecosystem-adoption">
        <div className="flex items-center gap-3">
          <div className="hv-icon-circle w-11 h-11 rounded-full flex items-center justify-center">
            <School size={20} strokeWidth={2.5} />
          </div>
          <span className="hv-badge">Your HikmahVerses pathway</span>
        </div>
        <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
          {PATHWAY.map((s) => {
            const Icon = s.icon;
            return (
              <div key={s.key} data-testid={`adoption-${s.key}`}
                   className={`rounded-2xl border-2 p-4 ${s.owned ? "border-hv-teal bg-[#E3F1EC]" : "border-[#E5DFC8] bg-white"}`}>
                <Icon size={20} strokeWidth={2.5} className={s.owned ? "text-hv-teal" : "text-hv-slate"} />
                <div className="hv-heading text-sm text-hv-navy mt-2 leading-snug">{s.name}</div>
                <div className="text-xs text-hv-slate mt-0.5">{s.ages}</div>
                <div className={`text-[10px] uppercase tracking-wider font-bold mt-2 ${s.owned ? "text-hv-teal" : "text-hv-slate/60"}`}>
                  {s.owned ? "In use" : "Not yet"}
                </div>
              </div>
            );
          })}
        </div>
        <p className="mt-4 text-hv-slate text-sm">
          Schools using 600 Stories most often add Tajweed next, once a cohort ages up.
          Adopting two or more programmes qualifies for bundle pricing.
        </p>
      </div>
    </main>
  );
}
